<script setup lang="ts">
import { ref } from 'vue';
import useValidators from 'src/shared/composables/useValidators';
import {
  mdiAccount,
  mdiClose,
  mdiEye,
  mdiEyeOff,
  mdiLock,
  mdiLockCheck,
  mdiEmail,
  mdiFacebook,
  mdiGoogle,
} from '@quasar/extras/mdi-v7';
import type { RegisterSignInFormData } from '../../views/RegisterSignInView/useRegisterSignInForm';
import BInput from 'src/shared/components/base/BInput/BInput.vue';
import BButton from 'src/shared/components/base/BButton/BButton.vue';
import type { StrapiProviders } from 'src/shared/composables/useStrapiApi';

const emits = defineEmits<{
  register: [data: RegisterSignInFormData];
  signIn: [data: RegisterSignInFormData];
  provider: [provider: StrapiProviders];
}>();

// Social providers enabled in Strapi; a button shows only when its provider is.
const { providers = [] } = defineProps<{ providers?: StrapiProviders[] }>();

const model = defineModel<RegisterSignInFormData>('modelValue', {
  required: true,
});

const registerSignInForm = ref();

const { required, email } = useValidators();
// const titleReverse = computed(() =>
//   model.value?.isRegister ? 'SIGN_IN' : 'REGISTER',
// );
const submit = async () => {
  const valid =
    registerSignInForm?.value && (await registerSignInForm.value.validate());
  if (!valid) return;

  if (model.value.isRegister) {
    emits('register', model.value);
  } else {
    emits('signIn', model.value);
  }
};

const visiblePassword = ref(false);
const visibleRePassword = ref(false);
</script>

<template>
  <q-card
    class="scroll full-vh"
    @keyup.enter="submit"
  >
    <q-card-section class="column items-center q-pb-none">
      <!-- White mark: this card only ever renders on the dark SET App page. -->
      <q-img
        src="/img/logo_set_white.svg"
        no-spinner
        alt="SET App"
        class="set-auth-logo"
      />
      <h3 class="text-h4 q-my-md q-px-sm text-center text-uppercase">
        {{
          !model.isRegister ? $t('ACCOUNT_LOGIN') : $t('ACCOUNT_REGISTRATION')
        }}
      </h3>
    </q-card-section>
    <q-card-section class="login-register-card">
      <q-form
        ref="registerSignInForm"
        class="q-px-sm q-pt-md q-gutter-y-lg"
        autocomplete="off"
      >
        <!-- Identif -->
        <BInput
          v-if="!model.isRegister"
          v-model="model.identifier"
          clearable
          :clear-icon="mdiClose"
          no-error-icon
          lazy-rules
          :rules="[
            required,
            (val: string) => val.length < 50 || $t('MAXIMUM_50_CHARACTERS'),
          ]"
          type="text"
          :label="$t('USERNAME_OR_EMAIL')"
          autocomplete="username"
        >
          <template #prepend>
            <q-icon :name="mdiAccount" />
          </template>
        </BInput>

        <!-- Email -->
        <BInput
          v-if="model.isRegister"
          v-model="model.email"
          clearable
          :clear-icon="mdiClose"
          no-error-icon
          type="email"
          lazy-rules
          :rules="[required, email]"
          :label="$t('EMAIL')"
        >
          <template #prepend>
            <q-icon :name="mdiEmail" />
          </template>
        </BInput>

        <!-- Password -->
        <BInput
          v-model="model.password"
          clearable
          :clear-icon="mdiClose"
          no-error-icon
          :type="visiblePassword ? 'text' : 'password'"
          lazy-rules
          :rules="[
            required,
            (val: string) => val.length < 50 || $t('MAXIMUM_50_CHARACTERS'),
          ]"
          :label="$t('PASSWORD')"
          :autocomplete="!model.isRegister ? 'password' : 'new-password'"
        >
          <template #prepend>
            <q-icon :name="mdiLock" />
          </template>
          <template #append>
            <q-icon
              :name="visiblePassword ? mdiEyeOff : mdiEye"
              class="cursor-pointer"
              @click="visiblePassword = !visiblePassword"
            />
          </template>
        </BInput>

        <!-- Retype Password -->
        <BInput
          v-if="model.isRegister"
          v-model="model.repassword"
          clearable
          :clear-icon="mdiClose"
          no-error-icon
          :type="visibleRePassword ? 'text' : 'password'"
          lazy-rules
          :rules="[required, (val: string) => val === model.password]"
          :label="$t('RETYPE_PASSWORD')"
        >
          <template #prepend>
            <q-icon :name="mdiLockCheck" />
          </template>
          <template #append>
            <q-icon
              :name="visibleRePassword ? mdiEyeOff : mdiEye"
              class="cursor-pointer"
              @click="visibleRePassword = !visibleRePassword"
            />
          </template>
        </BInput>
      </q-form>
    </q-card-section>
    <q-card-actions class="q-px-lg login-register-card">
      <BButton
        unelevated
        size="md"
        color="secondary"
        text-color="dark"
        shadow-color="#FFFFFF"
        class="full-width"
        :label="$t(model.isRegister ? 'REGISTER' : 'SIGN_IN')"
        @click.prevent="submit"
      />
    </q-card-actions>
    <q-card-section class="text-center q-pa-sm">
      <p class="text-grey-6 row justify-center items-center q-mb-none">
        {{ $t(model.isRegister ? 'HAVE_AN_ACCOUNT' : 'NEW_TO_OUR_SYSTEM') }}
        <q-btn
          flat
          size="sm"
          color="accent"
          text-color="accent"
          :label="$t(model.isRegister ? 'SIGN_IN' : 'REGISTER')"
          @click="model.isRegister = !model.isRegister"
        />
      </p>
      <p
        v-if="!model.isRegister"
        class="text-grey-6 row justify-center items-center"
      >
        <q-btn
          flat
          size="sm"
          class="q-my-none q-py-none"
          color="accent"
          :to="{ name: 'users.password.forgot' }"
          :label="$t('FORGOT_PASSWORD')"
        />
        <q-btn
          flat
          size="sm"
          class="q-my-none q-py-none"
          color="accent"
          :label="$t('CHILD_ACCOUNT')"
          :to="{ name: 'users.child.login' }"
        />
      </p>
    </q-card-section>
    <!-- Constrained to the form column: a full-bleed rule runs straight through
         the decorative art in the page corners. -->
    <q-separator
      v-if="providers.length"
      class="login-register-card"
    />
    <q-card-section
      v-if="providers.length"
      class="row items-center justify-center q-gap-sm q-gutter-md"
    >
      <q-btn
        v-if="providers.includes('facebook')"
        size="lg"
        round
        color="accent"
        text-color="dark"
        :icon="mdiFacebook"
        @click="emits('provider', 'facebook')"
      />
      <q-btn
        v-if="providers.includes('google')"
        size="lg"
        round
        color="accent"
        text-color="dark"
        :icon="mdiGoogle"
        @click="emits('provider', 'google')"
      />
    </q-card-section>
  </q-card>
</template>

<style>
.login-register-card {
  max-width: 400px;
  margin: auto;
}
.set-auth-logo {
  width: 11rem;
  max-width: 60vw;
}
.full-vh {
  height: 100vh;
}
</style>
